import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { WifiHigh, Eye, EyeSlash } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const DEPTS = ['Admin', 'General Manager', 'HR', 'Operator', 'IMFT', 'BMFT', 'Marketing', 'Finance', 'Store', 'NOC'];
const BRANDS = [
  { id: 'MyanmarLink', name: 'MyanmarLink', color: '#1877F2', icon: '🌐' },
  { id: 'BurmaNet', name: 'BurmaNet', color: '#E53935', icon: '🔴' },
  { id: 'OceanNet', name: 'OceanNet', color: '#00897B', icon: '🟢' },
];
export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [password, setPassword] = useState('');
  const [department, setDepartment] = useState('');
  const [brand, setBrand] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  if (user) { navigate('/dashboard'); return null; }
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brand) { toast.error('Please select a brand'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_id: staffId, password, department, brand: brand === 'All' ? '' : brand }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Login failed'); }
      const userData = await res.json();
      login(userData);
      toast.success(`Welcome, ${userData.name}!`);
      // Role-based redirect so BMFT/IMFT/NOC etc go to their own module
      const ROLE_HOME = {
        SuperAdmin: '/dashboard', Admin: '/dashboard', HR: '/hr', Operator: '/operator',
        IMFT: '/imft', 'IMFT Supervisor': '/imft', BMFT: '/bmft', 'BMFT Supervisor': '/bmft',
        Marketing: '/marketing', 'Sales Manager': '/marketing', 'Sales Staff': '/marketing',
        Finance: '/finance', Store: '/store', NOC: '/noc', 'General Manager': '/gm-dashboard',
      };
      const target = ROLE_HOME[userData.role] || '/dashboard';
      navigate(target);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };
  const selectedBrandObj = BRANDS.find(b => b.id === brand);
  return (
    <div className="min-h-screen bg-[var(--md-surface)] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-[440px]">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--md-primary)] rounded-2xl mb-3">
            <WifiHigh size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--md-on-surface)]">Myanmar Link ISP</h1>
          <p className="text-sm text-[var(--md-on-surface-variant)] mt-1">Field Team Mobile App</p>
        </div>
        <div className="md3-card p-6">
          <h2 className="text-lg font-medium text-center mb-4">Staff Login</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Staff ID</label>
              <input value={staffId} onChange={e => setStaffId(e.target.value)} placeholder="e.g. ML-OP01" required className="md3-input" data-testid="staff-id-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Password</label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required className="md3-input pr-10" data-testid="password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]">
                  {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Department</label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger className="bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-11" data-testid="dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent className="bg-white">{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Brand</label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger className="bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-11" data-testid="brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {BRANDS.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      <span className="flex items-center gap-2">
                        <span>{b.icon}</span>
                        <span>{b.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={loading || !brand} className="md3-filled-btn w-full py-3 disabled:opacity-60" data-testid="login-btn"
              style={selectedBrandObj ? { backgroundColor: selectedBrandObj.color } : {}}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
